import React, { useEffect, useState } from 'react';
import {
    DataGridPro,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarFilterButton,
    GridToolbarQuickFilter,
} from '@mui/x-data-grid-pro';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Button, Chip, Paper, Stack } from '@mui/material';
import ConfirmDeleteDialogHazard from './Modal/ConfirmDeleteHazard';
import EditMenuButton from './Modal/EditMenuButton';
import AuditChecklistApiService from '../api/AuditChecklistApiService';
import EditAuditChecklistModal from './Modal/Admin/EditAuditChecklistModal';
import AddIcon from '@mui/icons-material/Add';

const auditChecklistApiService = new AuditChecklistApiService();

const AuditChecklistAdmin = (props) => {
    const { setSnackbarProperties } = props;
    const [auditChecklists, setAuditChecklists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pageSize, setPageSize] = useState(100);
    const [currentSelectedAuditChecklist, setCurrentSelectedAuditChecklist] = useState();
    const [openEditAuditChecklistModal, setOpenEditAuditChecklistModal] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    function CustomToolbar() {
        return (
            <GridToolbarContainer sx={{ p: 1, justifyContent: 'space-between' }}>
                <Stack direction="row" spacing={1}>
                    <GridToolbarColumnsButton />
                    <GridToolbarFilterButton />
                </Stack>
                <GridToolbarQuickFilter debounceMs={300} />
            </GridToolbarContainer>
        );
    }

    const columns = [
        {
            field: 'acId',
            headerName: 'Id',
            flex: 0.2,
        },
        {
            field: 'acTitle',
            headerName: 'Title',
            flex: 1.2,
        },
        {
            field: 'organizationName',
            headerName: 'Organization',
            flex: 0.8,
        },
        {
            field: 'projectName',
            headerName: 'Location',
            flex: 0.8,
        },
        {
            field: 'auditChecklistItems',
            headerName: 'Items',
            flex: 0.4,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                const count = params.value?.length || 0;
                return (
                    <Chip
                        label={count}
                        size="small"
                        variant="outlined"
                    />
                );
            },
        },
        {
            field: 'acIsActive',
            headerName: 'Status',
            flex: 0.4,
            renderCell: (params) => (
                <Chip
                    label={params.row.acIsActive ? 'Active' : 'Inactive'}
                    size="small"
                    color={params.row.acIsActive ? 'success' : 'default'}
                    variant={params.row.acIsActive ? 'filled' : 'outlined'}
                />
            ),
        },
        {
            field: 'edit',
            headerName: '',
            flex: 0.2,
            filterable: false,
            sortable: false,
            renderCell: (params) => {
                return <EditMenuButton
                    params={params}
                    setShowDeleteDialog={setShowDeleteDialog}
                    setCurrentSelectedRecord={setCurrentSelectedAuditChecklist}
                    setShowEditDialog={setOpenEditAuditChecklistModal}
                    onEditFunc={() => {
                        setOpenEditAuditChecklistModal(true);
                        setCurrentSelectedAuditChecklist(params.row);
                    }}
                />;
            },
        },
    ];

    useEffect(() => {
        async function setupAuditChecklists () {
            await populateAuditChecklists();
        }

        void setupAuditChecklists();
    }, []);

    async function populateAuditChecklists () {
        setLoading(true);
        let auditChecklistResponse = await auditChecklistApiService.getAuditChecklists();
        setAuditChecklists(auditChecklistResponse.data);
        setLoading(false);
    }

    const dialogTitle = () => {
        return 'Confirm Delete Audit Checklist ' + currentSelectedAuditChecklist?.acTitle;
    };

    const dialogLabel = () => {
        return 'Are you sure you want to delete the Audit Checklist ' + currentSelectedAuditChecklist?.acTitle + '?';
    };

    async function onDeleteLocationClick (deleteValue) {
        if (deleteValue) {
            let deleteAuditChecklistResponse = await auditChecklistApiService.deleteAuditChecklist(currentSelectedAuditChecklist);

            if (deleteAuditChecklistResponse.success === true)
            {
                setSnackbarProperties('Successfully deleted Audit Checklist ' + currentSelectedAuditChecklist.acTitle);

                await populateAuditChecklists();
            }

            setShowDeleteDialog(false);
            setCurrentSelectedAuditChecklist(null);
        }
    };

    return (
        <div>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h4">
                    Audit Checklists
                </Typography>
                <Button
                    size="large"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenEditAuditChecklistModal(true)}
                >
                    Create New Checklist
                </Button>
            </Stack>
            <Paper elevation={1} sx={{ height: '80vh', width: '100%' }}>
                <DataGridPro
                    rows={auditChecklists}
                    columns={columns}
                    loading={loading}
                    pagination
                    pageSize={pageSize}
                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                    getRowId={(row) => row.acId}
                    components={{
                        Toolbar: CustomToolbar,
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100, 200]}
                    disableSelectionOnClick
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#fafafa',
                            borderBottom: '2px solid #e0e0e0',
                        },
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: '#f5f5f5',
                        },
                    }}
                />
            </Paper>
            <EditAuditChecklistModal
                open={openEditAuditChecklistModal}
                currentAuditChecklist={currentSelectedAuditChecklist}
                setSnackbarProperties={setSnackbarProperties}
                onCancel={() => {
                    setOpenEditAuditChecklistModal(false);
                    setCurrentSelectedAuditChecklist(null);
                }}
                onSave={async () => {
                    await populateAuditChecklists();
                    setOpenEditAuditChecklistModal(false);
                    setCurrentSelectedAuditChecklist(null);
                }}
            />
            <ConfirmDeleteDialogHazard
                keepMounted
                value={currentSelectedAuditChecklist}
                open={showDeleteDialog}
                onClose={onDeleteLocationClick}
                deleteTitle={dialogTitle()}
                deleteLabel={dialogLabel()}
            ></ConfirmDeleteDialogHazard>
        </div>
    );
};
export default AuditChecklistAdmin;
